import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../context/I18nContext';
import { EmailTemplate } from '../../types';
import BaseFormModal from '../ui/BaseFormModal';
import Input from '../ui/Input';
import TextArea from '../ui/TextArea';
import Select from '../ui/Select';
import VariableChips from './VariableChips';
import { insertTextAtCursor } from '../../utils/messageVariables';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Omit<EmailTemplate, 'id'>) => void;
  template?: EmailTemplate | null;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  template,
}) => {
  const { t } = useI18n();
  const [nameError, setNameError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    type: 'lead_generation' as EmailTemplate['type'],
  });
  const [activeField, setActiveField] = useState<'subject' | 'content'>('content');
  const subjectRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNameError(null);
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        content: template.content,
        type: template.type,
      });
    } else {
      setFormData({
        name: '',
        subject: '',
        content: '',
        type: 'lead_generation' as EmailTemplate['type'],
      });
    }
  }, [template]);

  const validateAndSubmit = () => {
    const name = formData.name.trim();

    let hasError = false;
    if (!name) {
      setNameError(t('messages.templateNameRequired'));
      hasError = true;
    }
    if (hasError) return;

    const templateData: Omit<EmailTemplate, 'id'> = {
      name,
      subject: formData.subject || '',
      content: formData.content || '',
      type: formData.type,
    };

    onClose();

    Promise.resolve(onSave(templateData)).catch((error) => {
      console.error('Error saving template:', error);
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateAndSubmit();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      subject: '',
      content: '',
      type: 'lead_generation' as EmailTemplate['type'],
    });
    onClose();
  };

  const insertVariable = (token: string) => {
    if (activeField === 'subject') {
      insertTextAtCursor(subjectRef.current, token, formData.subject, (next) =>
        setFormData((prev) => ({ ...prev, subject: next }))
      );
    } else {
      insertTextAtCursor(contentRef.current, token, formData.content, (next) =>
        setFormData((prev) => ({ ...prev, content: next }))
      );
    }
  };

  const typeOptions = [
    { value: 'lead_generation', label: t('messages.type.leadGeneration') },
    { value: 'follow_up', label: t('messages.type.followUp') },
    { value: 'negotiation', label: t('messages.type.negotiation') },
    { value: 'delivery', label: t('messages.type.delivery') },
  ];

  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={handleClose}
      resourceKey="messages"
      size="xl"
      isLoading={false}
      mode={template ? 'update' : 'create'}
      onValidate={validateAndSubmit}
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('messages.templateNameLabel')}
            placeholder={t('messages.templateNamePlaceholder')}
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (nameError) setNameError(null);
            }}
            error={nameError || undefined}
          />

          <Select
            label={t('messages.typeLabel')}
            value={formData.type}
            onChange={(value) => setFormData({ ...formData, type: value as EmailTemplate['type'] })}
            options={typeOptions}
          />
        </div>

        <Input
          ref={subjectRef}
          label={t('messages.emailSubjectLabel')}
          placeholder={t('messages.emailSubjectPlaceholder')}
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          onFocus={() => setActiveField('subject')}
        />

        <TextArea
          ref={contentRef}
          label={t('messages.contentLabel')}
          placeholder={t('messages.contentPlaceholder')}
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          onFocus={() => setActiveField('content')}
          rows={12}
        />

        <div className="rounded-xl border border-gray-200/80 bg-gray-50/80 p-4">
          <VariableChips onInsert={insertVariable} projectEnabled />
        </div>
      </form>
    </BaseFormModal>
  );
};

export default TemplateModal;
